/** Factored out of library to avoid circular references */

import { logger } from '@tunasong/models'
import { NoteLib } from '../note/index.js'
import { isChordNotes, isChordVariant } from './chord-types.js'
import type { Chord, ChordVariant } from './chord-types.js'

/**
 * Legacy persisted documents store a major triad as `variant: null` (or omit the
 * field) instead of the canonical `variant: ''`. `isChordVariant` rightly rejects
 * those non-string variants — it protects consumers that run string methods on
 * `variant` (GH #1510). For display, though, a root-bearing node with a missing
 * variant is simply a major triad, so coerce it to `variant: ''` rather than
 * rendering "- UNKNOWN -" (GH #1556). Anything without a string root is untouched.
 */
const coerceMajorTriad = (chord: Chord): Chord => {
  const { root, variant } = chord as { root?: unknown; variant?: unknown }
  if (typeof root === 'string' && (variant === null || variant === undefined)) {
    return { ...chord, variant: '' } as ChordVariant
  }
  return chord
}

export const getName = (chord?: Chord, transposeSemiTones = 0) => {
  if (!chord) {
    return '-'
  }
  const isTransposed = transposeSemiTones > 0 || transposeSemiTones < 0
  const transposeSign = transposeSemiTones > 0 ? '+' : ''
  const transposeLabel = isTransposed ? ` (${transposeSign}${transposeSemiTones})` : ''

  const normalized = coerceMajorTriad(chord)
  if (isChordVariant(normalized)) {
    const root = NoteLib.transpose(normalized.root, transposeSemiTones)
    const variant = normalized.variant ? normalized.variant : ''
    const bassNote = normalized.bassNote ? `/${NoteLib.transpose(normalized.bassNote, transposeSemiTones)}` : ''

    return `${root}${variant}${bassNote}${transposeLabel}`
  }
  if (isChordNotes(normalized)) {
    return `${normalized.name}${transposeLabel}`
  }
  logger.error(`Unknown chord shape - cannot transpose`, chord)
  return '- UNKNOWN -'
}
